/* eslint-disable @nx/enforce-module-boundaries */
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Icon, TextEllipsis } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import useHover from 'ahooks/lib/useHover';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  ClearButtonType,
  ClearTagsButton,
} from '../../components/filter-tags/components/clear-tags-button';
import { useFilterTags } from '../../components/filter-tags/hooks/use-filter-tags';
import {
  buildClearAllDraft,
  getTagsCount,
} from '../../components/filter-tags/utils';
import { SearchFilter } from '../../types';
import { AutocompleteFilterContent } from './autocomplete-filter-content';
import { AdvancedBulkInputDialog } from './components/advanced-bulk-input-dialog';
import * as SX from './sx';
import type { AutocompleteFilterProps as Props } from './types';

export const AutocompleteFilter = memo((props: Props) => {
  const { setParams, params } = useSearchQueryFilter({
    select: [props.field!],
  });
  const {
    onClearButtonClick: overrideOnClearButtonClick,
    showClearButton: overrideShowClearButton,
  } = props;
  const tags = useFilterTags(params, props as SearchFilter);
  const count = getTagsCount(tags);
  const hoverRef = useRef<HTMLDivElement>(null);
  const hover = useHover(hoverRef);
  const advancedDialogState = useBoolean(false);
  const { onTrue: openAdvancedDialog } = advancedDialogState;

  const showClearButton = useMemo(
    () => overrideShowClearButton || (hover && !!count),
    [overrideShowClearButton, hover, count]
  );

  const handleClear = useCallback(() => {
    const draft = buildClearAllDraft(tags);
    setParams(draft);
  }, [tags, setParams]);

  const handleClearButtonClick = useCallback(() => {
    if (overrideOnClearButtonClick) {
      overrideOnClearButtonClick();
    } else {
      handleClear();
    }
  }, [overrideOnClearButtonClick, handleClear]);

  const handleAdvancedButtonMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    },
    []
  );

  return (
    <Box
      className={classnames('AutocompleteFilter', props.className)}
      sx={SX.getRootSx(props.metadata?.styles?.sx)}
      ref={hoverRef}
    >
      {props.name && (
        <Box sx={SX.header}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <TextEllipsis
              variant="body3"
              color="grey.600"
              fontWeight={600}
              value={props.name}
              w={showClearButton ? '210px' : '240px'}
              lineHeight="24px"
            />
            {props.metadata?.info && (
              <Tooltip
                title={props.metadata?.info}
                arrow
                placement="right"
                maxWidth="unset"
                enterDelay={200}
              >
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Icon name="info" size={16} color="grey.400" />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Stack direction="row" gap={0.5}>
            {showClearButton && (
              <ClearTagsButton
                type={ClearButtonType.ICON_BUTTON}
                count={count || 1}
                onClick={handleClearButtonClick}
              />
            )}
            {props.metadata?.advancedButtonVisible && (
              <Button
                variant="contained"
                color="primary"
                size="xsmall"
                onClick={openAdvancedDialog}
                onMouseDown={handleAdvancedButtonMouseDown}
              >
                Advanced
              </Button>
            )}
          </Stack>
        </Box>
      )}
      <Box sx={SX.content}>
        <AutocompleteFilterContent {...props} />
      </Box>
      {props.metadata?.advancedButtonVisible && (
        <AdvancedBulkInputDialog
          field={props.field!}
          dialogState={advancedDialogState}
          metadata={props.metadata}
          filterType={props.type}
          filterName={props.name}
        />
      )}
    </Box>
  );
});

AutocompleteFilter.displayName = 'AutocompleteFilter';
